const breadcrumbs = [];
const recentReports = new Map();
let installed = false;
let nativeFetch;
let sentThisSession = 0;

const MAX_REPORTS_PER_SESSION = 30;
const MAX_PAYLOAD_BYTES = 28 * 1024;
const FIRST_PARTY_BASE_DOMAINS = [
  'revieworks.com',
  'reviewed.co.kr',
  'lawkit.kr',
  'medilawyer.co.kr',
];

const release = import.meta.env.VITE_RELEASE || 'unknown';
const explicitEndpoint = import.meta.env.VITE_CLIENT_ERROR_ENDPOINT || '';

function endpointFor(hostname) {
  if (explicitEndpoint) return explicitEndpoint;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return '';
  const gatewayLabel = host
    .split('.')
    .some((label) => ['dev', 'stage', 'staging', 'imsi', 'next'].includes(label))
    ? 'gateway.dev'
    : 'gateway';
  if (host === 'contents.revieworks.com' || host.endsWith('.contents.revieworks.com')) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`;
  }
  if (host === 'revieworks.com' || host.endsWith('.revieworks.com')) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`;
  }
  if (host === 'reviewed.co.kr' || host.endsWith('.reviewed.co.kr')) {
    return `https://${gatewayLabel}.reviewed.co.kr/client-errors`;
  }
  if (host === 'lawkit.kr' || host.endsWith('.lawkit.kr')) {
    return `https://${gatewayLabel}.lawkit.kr/client-errors`;
  }
  if (host === 'medilawyer.co.kr' || host.endsWith('.medilawyer.co.kr')) {
    return `https://${gatewayLabel}.medilawyer.co.kr/client-errors`;
  }
  return '';
}

function pathOnly(value) {
  try {
    return new URL(String(value), window.location.origin).pathname || '/';
  } catch {
    return String(value).split(/[?#]/, 1)[0].slice(0, 500);
  }
}

function isFirstPartyRequest(value) {
  try {
    const url = new URL(String(value), window.location.origin);
    if (url.origin === window.location.origin) return true;
    const host = url.hostname.toLowerCase();
    return FIRST_PARTY_BASE_DOMAINS.some((base) => host === base || host.endsWith(`.${base}`));
  } catch {
    return false;
  }
}

function isHttpError(status) {
  return status >= 400 && status <= 599;
}

function safeErrorText(value, limit) {
  const withoutUrlQueries = value.replace(/https?:\/\/[^\s)\]}]+/g, (candidate) => {
    try {
      const url = new URL(candidate);
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return candidate.split(/[?#]/, 1)[0];
    }
  });
  const withoutRelativeUrlQueries = withoutUrlQueries.replace(
    /((?:\.{0,2}\/)[^\s?#)\]}]+)[?#][^\s)\]}]*/g,
    '$1'
  );
  return withoutRelativeUrlQueries
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED_EMAIL]')
    .replace(/(?:\+?82[- ]?)?0?1[016789][-. ]?\d{3,4}[-. ]?\d{4}/g, '[REDACTED_PHONE]')
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [REDACTED_TOKEN]')
    .slice(0, limit);
}

function errorFrom(value) {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value.slice(0, 1500));
  return new Error(`Unhandled rejection (${Object.prototype.toString.call(value)})`);
}

function isNoise(error) {
  return (
    error.name === 'AbortError' ||
    /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(
      error.message
    ) ||
    /^(Script error\.?|Load failed)$/i.test(error.message)
  );
}

function remember(type, request) {
  breadcrumbs.push({
    time: new Date().toISOString(),
    type,
    method: (request.method || 'GET').slice(0, 12),
    path: pathOnly(request.path || '/'),
    status: request.status || 0,
  });
  if (breadcrumbs.length > 20) breadcrumbs.splice(0, breadcrumbs.length - 20);
}

function responseRequestId(headers) {
  return (
    headers.get('x-request-id') || headers.get('x-amzn-requestid') || headers.get('trace-id') || ''
  );
}

function shouldSend(error, context) {
  if (sentThisSession >= MAX_REPORTS_PER_SESSION) return false;
  const firstFrame = (error.stack || '').split('\n')[1] || '';
  const key = [
    context.source,
    error.name,
    error.message,
    firstFrame,
    context.request?.path,
    context.request?.status,
  ].join('|');
  const now = Date.now();
  const previous = recentReports.get(key) || 0;
  recentReports.set(key, now);
  if (recentReports.size > 100) {
    for (const [candidate, time] of recentReports) {
      if (now - time > 60000) recentReports.delete(candidate);
    }
    while (recentReports.size > 100) {
      const oldest = recentReports.keys().next().value;
      if (oldest === undefined) break;
      recentReports.delete(oldest);
    }
  }
  if (now - previous <= 5000) return false;
  sentThisSession += 1;
  return true;
}

function serializeWithinLimit(payload) {
  let body = JSON.stringify(payload);
  if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body;

  for (const [stack, componentStack, crumbCount] of [
    [6000, 2500, 10],
    [3000, 1200, 5],
    [1000, 400, 2],
    [0, 0, 0],
  ]) {
    body = JSON.stringify({
      ...payload,
      error: {
        ...payload.error,
        message: stack === 0 ? payload.error.message.slice(0, 500) : payload.error.message,
        stack: payload.error.stack.slice(0, stack),
        componentStack: payload.error.componentStack.slice(0, componentStack),
      },
      breadcrumbs: crumbCount === 0 ? [] : payload.breadcrumbs.slice(-crumbCount),
    });
    if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body;
  }
  return '';
}

function send(payload) {
  const endpoint = endpointFor(window.location.hostname);
  if (!endpoint) return;
  const body = serializeWithinLimit(payload);
  if (!body) return;
  try {
    if (navigator.sendBeacon?.(endpoint, body)) return;
  } catch {
    // fetch keepalive fallback below
  }
  const sendFetch = nativeFetch || window.fetch.bind(window);
  try {
    Promise.resolve(
      sendFetch(endpoint, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        credentials: 'omit',
        mode: 'cors',
      })
    ).catch(() => undefined);
  } catch {
    // 수집 실패는 원래 사용자 흐름에 절대 전파하지 않는다.
  }
}

export function reportFrontendError(value, context) {
  try {
    if (typeof window === 'undefined') return;
    const error = errorFrom(value);
    if (isNoise(error) || !shouldSend(error, context)) return;
    const digest = context.digest || error.digest || '';
    send({
      occurredAt: new Date().toISOString(),
      release,
      source: context.source,
      error: {
        name: error.name || 'Error',
        message: safeErrorText(error.message || 'No message provided.', 1500),
        stack: safeErrorText(error.stack || '', 12000),
        componentStack: safeErrorText(context.componentStack || '', 6000),
        digest: safeErrorText(digest, 160),
      },
      page: { path: window.location.pathname },
      request: context.request || {},
      client: {
        language: navigator.language || '',
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      },
      breadcrumbs: breadcrumbs.slice(-20),
    });
  } catch {
    // 오류 reporter 자체가 애플리케이션 오류가 되어서는 안 된다.
  }
}

function installFetchMonitor() {
  nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    let method = 'GET';
    let path = '';
    let firstParty = false;
    try {
      const requestUrl = input instanceof Request ? input.url : String(input);
      method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      path = pathOnly(requestUrl);
      firstParty = isFirstPartyRequest(requestUrl);
    } catch {
      // 메타데이터 추출 실패와 무관하게 원래 fetch는 그대로 실행한다.
    }
    try {
      const response = await nativeFetch(input, init);
      try {
        if (firstParty) {
          const request = {
            method,
            path,
            status: response.status,
            requestId: responseRequestId(response.headers),
          };
          remember('fetch', request);
          if (isHttpError(response.status)) {
            reportFrontendError(
              new Error(`${method} ${path} failed with HTTP ${response.status}`),
              {
                source: 'fetch',
                request,
              }
            );
          }
        }
      } catch {
        // 원래 fetch response를 보존한다.
      }
      return response;
    } catch (failure) {
      if (firstParty && !(failure instanceof DOMException && failure.name === 'AbortError')) {
        try {
          const request = { method, path, status: 0 };
          remember('fetch', request);
          reportFrontendError(failure, { source: 'fetch', request });
        } catch {
          // 원래 fetch rejection을 보존한다.
        }
      }
      throw failure;
    }
  };
}

function installXhrMonitor() {
  const metadata = new WeakMap();
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function monitoredOpen(method, url, ...args) {
    const result = originalOpen.call(this, method, url, ...args);
    metadata.delete(this);
    try {
      if (isFirstPartyRequest(url)) {
        metadata.set(this, { method: method.toUpperCase(), path: pathOnly(url), aborted: false });
      }
    } catch {
      // 원래 XHR open 성공 여부에 영향을 주지 않는다.
    }
    return result;
  };

  XMLHttpRequest.prototype.send = function monitoredSend(body) {
    const meta = metadata.get(this);
    if (meta) {
      try {
        this.addEventListener('abort', () => {
          meta.aborted = true;
        });
        this.addEventListener(
          'loadend',
          () => {
            try {
              const request = {
                method: meta.method,
                path: meta.path,
                status: this.status || 0,
                requestId:
                  this.getResponseHeader('x-request-id') ||
                  this.getResponseHeader('x-amzn-requestid') ||
                  '',
              };
              remember('xhr', request);
              if (!meta.aborted && (this.status === 0 || isHttpError(this.status))) {
                reportFrontendError(
                  new Error(
                    `${meta.method} ${meta.path} failed with HTTP ${this.status || 'network'}`
                  ),
                  {
                    source: 'xhr',
                    request,
                  }
                );
              }
            } catch {
              // XHR loadend consumer에 reporter 오류를 노출하지 않는다.
            }
          },
          { once: true }
        );
      } catch {
        // listener 설치 실패와 무관하게 원래 XHR send는 그대로 실행한다.
      }
    }
    return originalSend.call(this, body);
  };
}

export function installFrontendErrorReporter() {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  try {
    installFetchMonitor();
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    installXhrMonitor();
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    window.addEventListener('error', (event) => {
      if (event.error) reportFrontendError(event.error, { source: 'window.error' });
    });
    window.addEventListener('unhandledrejection', (event) => {
      reportFrontendError(event.reason, { source: 'unhandledrejection' });
    });
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
}
